import { defaultLanguage, LanguageDetails } from '@/types/localisation';
import { ActiveMessage, ActiveMessageAnswers, ErrorModel, HistoryMessages, MessageModel, MessageModelType } from '@/types/message';
import { addMissingMessage, areMessagesEqual, messagesContainMessage } from '@/utils/contextUtils/addMissingMessage';
import { isActivatableHistoryMessage } from '@/utils/contextUtils/isActivatableHistoryMessage';
import { createContext, Dispatch, useContext, useReducer } from 'react';

export interface ChatContext {
  messages: MessageModel[];
  activeMessage: ActiveMessage | null;
  activeError: ErrorModel | null;
  language: LanguageDetails;
  isSurveyTesting: boolean;
}

export enum ChatActionKind {
  ADD_MESSAGE = 'ADD_MESSAGE',
  ADD_NON_ACTIVE_MESSAGE = 'ADD_NON_ACTIVE_MESSAGE',
  UPDATE_ACTIVE_MESSAGE = 'UPDATE_ACTIVE_MESSAGE',
  UPDATE_MEDIA_UNLOCKED = 'UPDATE_MEDIA_UNLOCKED',
  RESET_CHAT = 'RESET_CHAT',
  UPDATE_ACTIVE_ERROR = 'UPDATE_ACTIVE_ERROR',
  UPDATE_LANGUAGE = 'UPDATE_LANGUAGE',
  UPDATE_IS_SURVEY_TESTING = 'UPDATE_IS_SURVEY_TESTING',
  RESET_MESSAGES = 'RESET_MESSAGES',
  SET_HISTORY_MESSAGES = 'SET_HISTORY_MESSAGES'

  // Add more when needed
}

export type ChatAction =
  | AddMessageAction
  | AddNonActiveMessageAction
  | UpdateActiveMessageAction
  | UpdateMediaUnlockedAction
  | ResetChatAction
  | UpdateActiveErrorAction
  | UpdateLanguageAction
  | UpdateIsSurveyTesting
  | ResetMessages
  | SetHistoryMessages;

export interface AddMessageAction {
  type: ChatActionKind.ADD_MESSAGE;
  payload: MessageModel;
}

export interface AddNonActiveMessageAction {
  type: ChatActionKind.ADD_NON_ACTIVE_MESSAGE;
  payload: MessageModel;
}

export interface UpdateActiveMessageAction {
  type: ChatActionKind.UPDATE_ACTIVE_MESSAGE;
  payload: ActiveMessageAnswers;
}

export interface UpdateMediaUnlockedAction {
  type: ChatActionKind.UPDATE_MEDIA_UNLOCKED;
  payload: boolean;
}

export interface ResetChatAction {
  type: ChatActionKind.RESET_CHAT;
}

export interface UpdateActiveErrorAction {
  type: ChatActionKind.UPDATE_ACTIVE_ERROR;
  payload: ErrorModel;
}

export interface UpdateLanguageAction {
  type: ChatActionKind.UPDATE_LANGUAGE;
  payload: LanguageDetails;
}

export interface UpdateIsSurveyTesting {
  type: ChatActionKind.UPDATE_IS_SURVEY_TESTING;
  payload: boolean;
}

export interface ResetMessages {
  type: ChatActionKind.RESET_MESSAGES;
}

export interface SetHistoryMessages {
  type: ChatActionKind.SET_HISTORY_MESSAGES;
  payload: HistoryMessages[];
}

export const defaultChatState: ChatContext = {
  messages: [],
  activeMessage: null,
  activeError: null,
  language: defaultLanguage,
  isSurveyTesting: false
};

function getCurrentHistoryMessage(messages: HistoryMessages[]): HistoryMessages | null {
  const lastNonLanguageMessage = messages.findLast((message) => message.kind !== MessageModelType.LanguageSwitched);

  return lastNonLanguageMessage && isActivatableHistoryMessage(lastNonLanguageMessage) ? lastNonLanguageMessage : null;
}

export function chatReducer(state: ChatContext, action: ChatAction): ChatContext {
  switch (action.type) {
    case ChatActionKind.ADD_MESSAGE: {
      if (messagesContainMessage(state.messages, action.payload)) {
        // Same kind+id already in messages - don't update activeMessage to preserve selected answers
        return state;
      }

      return {
        ...state,
        messages: addMissingMessage(state.messages, action.payload),
        activeMessage: { message: action.payload, shownAt: Date.now() }
      };
    }

    case ChatActionKind.ADD_NON_ACTIVE_MESSAGE: {
      if (messagesContainMessage(state.messages, action.payload)) {
        return state;
      }

      return {
        ...state,
        messages: addMissingMessage(state.messages, action.payload)
      };
    }

    case ChatActionKind.UPDATE_ACTIVE_MESSAGE:
      return {
        ...state,
        activeMessage: state.activeMessage ? { ...state.activeMessage, answers: action.payload } : state.activeMessage,
        activeError: null
      };

    case ChatActionKind.UPDATE_MEDIA_UNLOCKED:
      return {
        ...state,
        activeMessage: state.activeMessage ? { ...state.activeMessage, isMediaUnlocked: action.payload } : state.activeMessage
      };

    case ChatActionKind.RESET_CHAT:
      return defaultChatState;

    case ChatActionKind.UPDATE_ACTIVE_ERROR:
      return {
        ...state,
        activeMessage: state.activeMessage,
        activeError: action.payload
      };

    case ChatActionKind.UPDATE_LANGUAGE:
      return {
        ...state,
        language: action.payload
      };

    case ChatActionKind.UPDATE_IS_SURVEY_TESTING:
      return {
        ...state,
        isSurveyTesting: action.payload
      };

    case ChatActionKind.RESET_MESSAGES:
      return {
        ...state,
        messages: defaultChatState.messages,
        activeMessage: defaultChatState.activeMessage
      };

    case ChatActionKind.SET_HISTORY_MESSAGES: {
      // Safety check: Only update if messages are different (primary check is in the hook)
      if (areMessagesEqual(state.messages, action.payload)) {
        return state;
      }
      // A trailing LanguageSwitched records metadata about the still-current prompt. Answer
      // summaries are different: they mark the prompt as already answered, so do not walk past them.
      const currentHistoryMessage = getCurrentHistoryMessage(action.payload);
      const previousActive = state.activeMessage;

      const isSameActiveMedia =
        previousActive?.message.kind === MessageModelType.Media &&
        currentHistoryMessage?.kind === MessageModelType.Media &&
        previousActive.message.id === currentHistoryMessage.id;

      const shouldPreserveMediaUnlock = isSameActiveMedia && previousActive.isMediaUnlocked === true;

      return {
        ...state,
        messages: action.payload,
        activeMessage: currentHistoryMessage
          ? { message: currentHistoryMessage, shownAt: Date.now(), ...(shouldPreserveMediaUnlock ? { isMediaUnlocked: true } : {}) }
          : null
      };
    }

    default:
      throw new Error(`Unhandled action type: ${(action as ChatAction).type}`);
  }
}
export const chatDispatchContext = createContext<Dispatch<ChatAction>>(null as never);
export const chatContext = createContext<ChatContext>(null as never);

type ChatProviderProps = { children: React.ReactNode; optionalContext?: ChatContext | undefined };

export function ChatProvider({ children, optionalContext }: ChatProviderProps) {
  const initialState = optionalContext ? optionalContext : defaultChatState;

  const [{ messages, activeMessage, activeError, language, isSurveyTesting }, dispatch] = useReducer(chatReducer, initialState);

  return (
    <chatDispatchContext.Provider value={dispatch}>
      <chatContext.Provider value={{ messages, activeMessage, activeError, language, isSurveyTesting }}>{children}</chatContext.Provider>
    </chatDispatchContext.Provider>
  );
}

export const useChat = () => {
  const context = useContext(chatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const useChatDispatch = () => {
  const context = useContext(chatDispatchContext);
  if (context === undefined) {
    throw new Error('useChatDispatch must be used within a ChatProvider');
  }
  return context;
};
